import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paper sizes in millimetres (width × height). The CSS @page rule will
// switch the printer to the chosen sheet and each card will fill it.
const PAPER_SIZES = [
  { id: 'square_65', label: '6.5 × 6.5 cm square (recommended)', w: 65, h: 65 },
  { id: 'sheet_a4',  label: 'A4 sheet — 6 cards (2 complete sets per page)', w: 210, h: 297, sheet: true },
  { id: 'square_70', label: '7 × 7 cm square', w: 70, h: 70 },
  { id: 'square_90', label: '9 × 9 cm square', w: 90, h: 90 },
  { id: 'a6', label: 'A6 — 10.5 × 14.8 cm', w: 105, h: 148 },
  { id: 'a5', label: 'A5 — 14.8 × 21 cm', w: 148, h: 210 },
  { id: 'a4', label: 'A4 — 21 × 29.7 cm (one card per page)', w: 210, h: 297 },
  { id: 'business', label: 'Business card — 8.5 × 5.5 cm', w: 85, h: 55 },
];

const CARD_LABELS = {
  inspiration: 'Inspiration',
  details: 'About this piece',
  message: 'A little message',
};

export default function KeepsakeCardsPrintPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paperId, setPaperId] = useState('square_65');
  const paper = PAPER_SIZES.find((p) => p.id === paperId) || PAPER_SIZES[0];

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const absolute = (path) => (path?.startsWith('/api/') ? `${backendUrl}${path}` : path);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [productRes, cardsRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/products/${productId}/keepsake-cards`),
        ]);
        if (cancelled) return;
        setProduct(productRes.data);
        setCards(cardsRes.data?.cards || null);
      } catch (error) {
        if (cancelled) return;
        logger.error('Could not load print page:', error);
        toast.error('Could not load cards');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await axios.post(
        `${API}/products/${productId}/keepsake-cards/regenerate`,
        {},
        { timeout: 60000 },
      );
      setCards(response.data?.cards || null);
      toast.success('Cards refreshed from current product copy');
    } catch (error) {
      logger.error('Refresh failed:', error);
      toast.error('Could not refresh cards');
    } finally {
      setRefreshing(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="bg-[#F5F5F0] min-h-screen" data-testid="keepsake-print-page">
      {/* Toolbar — hidden when printing */}
      <header className="print-hidden bg-white border-b border-[#CAD2C5]/60">
        <div className="container-custom px-6 md:px-10 py-6 max-w-5xl mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link
              to="/admin/products"
              className="text-caption text-[#84A98C] hover:text-[#52796F] transition-colors inline-flex items-center gap-2 mb-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
              Back to products
            </Link>
            <h1 className="font-serif text-2xl text-[#354F52]">
              Print keepsake cards
            </h1>
            {product && (
              <p className="text-sm text-[#52796F] mt-1">{product.name}</p>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-[#84A98C]">Paper size</Label>
              <Select value={paperId} onValueChange={setPaperId}>
                <SelectTrigger className="w-[260px]" data-testid="print-paper-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAPER_SIZES.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="flex items-center gap-2"
              data-testid="print-refresh-btn"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
              )}
              Refresh
            </Button>
            <Button
              onClick={handlePrint}
              disabled={loading || !cards}
              className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6] flex items-center gap-2"
              data-testid="print-trigger-btn"
            >
              <Printer className="w-4 h-4" strokeWidth={1.5} />
              Print
            </Button>
          </div>
        </div>
        <div className="container-custom px-6 md:px-10 pb-5 max-w-5xl mx-auto">
          <p className="text-xs text-[#84A98C] italic">
            Best on glossy 6.5 × 6.5 cm photo paper. In the browser print dialog set
            <strong> Margins: None</strong> and tick <strong>Background graphics</strong> so the
            cream paper colour prints. Choose the <strong>A4 sheet</strong> option to print
            two complete sets (six cards) per sheet — re-feed the same paper through your
            printer for even more sets.
          </p>
        </div>
      </header>

      {/* Print sheets — one per card, or all three on one A4 sheet with crop marks */}
      <main className="py-10">
        {loading ? (
          <p className="text-center text-[#84A98C] italic">Loading cards…</p>
        ) : !cards ? (
          <p className="text-center text-[#84A98C] italic">No cards available.</p>
        ) : paper.sheet ? (
          /* ─── A4 sheet layout: 2 rows × 3 columns = 2 sets of 3 cards ─── */
          <div className="flex justify-center" data-testid="print-sheets">
            <article
              className="print-sheet bg-white shadow-sm border border-[#CAD2C5]/40 relative"
              style={{ width: `${paper.w}mm`, height: `${paper.h}mm` }}
              data-testid="print-sheet-a4"
            >
              <div
                className="absolute"
                style={{
                  top: '18mm',
                  left: '15mm',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 65mm)',
                  gridTemplateRows: 'repeat(2, 65mm)',
                  columnGap: '8mm',
                  rowGap: '12mm',
                }}
              >
                {[0, 1].flatMap((setIdx) =>
                  ['inspiration', 'details', 'message'].map((key) => {
                    const url = cards[key];
                    if (!url) return null;
                    return (
                      <div
                        key={`${key}-${setIdx}`}
                        className="relative"
                        style={{ width: '65mm', height: '65mm' }}
                        data-testid={`print-card-${key}-${setIdx}`}
                      >
                        {/* Crop marks at each corner */}
                        {[
                          { top: '-4mm', left: '0', borderLeft: '0.3mm solid #999', height: '3mm', width: '0' },
                          { top: '0', left: '-4mm', borderTop: '0.3mm solid #999', width: '3mm', height: '0' },
                          { top: '-4mm', right: '0', borderRight: '0.3mm solid #999', height: '3mm', width: '0' },
                          { top: '0', right: '-4mm', borderTop: '0.3mm solid #999', width: '3mm', height: '0' },
                          { bottom: '-4mm', left: '0', borderLeft: '0.3mm solid #999', height: '3mm', width: '0' },
                          { bottom: '0', left: '-4mm', borderBottom: '0.3mm solid #999', width: '3mm', height: '0' },
                          { bottom: '-4mm', right: '0', borderRight: '0.3mm solid #999', height: '3mm', width: '0' },
                          { bottom: '0', right: '-4mm', borderBottom: '0.3mm solid #999', width: '3mm', height: '0' },
                        ].map((style, i) => (
                          <span key={i} className="absolute" style={style} />
                        ))}
                        <img
                          src={absolute(url)}
                          alt={CARD_LABELS[key]}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </div>
                    );
                  }),
                )}
              </div>
              <p
                className="print-hidden absolute text-xs text-[#84A98C] italic text-center"
                style={{ bottom: '8mm', left: 0, right: 0 }}
              >
                Two complete sets of 3 cards per sheet · cut along the crop marks
              </p>
            </article>
          </div>
        ) : (
          /* ─── Single card per page ─── */
          <div className="space-y-10 flex flex-col items-center" data-testid="print-sheets">
            {['inspiration', 'details', 'message'].map((key) => {
              const url = cards[key];
              if (!url) return null;
              return (
                <article
                  key={key}
                  className="print-sheet bg-white shadow-sm border border-[#CAD2C5]/40"
                  style={{
                    width: `${paper.w}mm`,
                    height: `${paper.h}mm`,
                  }}
                  data-testid={`print-card-${key}`}
                >
                  <img
                    src={absolute(url)}
                    alt={CARD_LABELS[key]}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </article>
              );
            })}
          </div>
        )}
      </main>

      {/* Print rules — paper size, hide UI, one card per page (or all on one A4 sheet) */}
      <style>{`
        @media print {
          @page {
            size: ${paper.w}mm ${paper.h}mm;
            margin: 0;
          }
          html, body { background: #fff !important; }
          .print-hidden { display: none !important; }
          main { padding: 0 !important; }
          .print-sheet {
            box-shadow: none !important;
            border: none !important;
            ${paper.sheet
              ? 'page-break-after: auto; break-after: auto;'
              : 'page-break-after: always; break-after: page;'}
            margin: 0 !important;
          }
          .print-sheet:last-child {
            page-break-after: auto;
            break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}
